import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { startPaymentCheckout } from '../../../helpers/paymentCheckout';
import { daysOf, packageIdOf, promotionDiscount, totalsOf } from '../pricing';
import { getPromotion, subscribe } from '../services/checkout.service';
import { getPackagePolicy } from '../services/policy.service';

const NO_CODE = { discount: 0, checking: false, note: null };

/**
 * Everything the checkout dialogue knows: the receipt, the promotion code, the
 * package's policy and the request that subscribes.
 *
 * The behaviour is the old checkout form's (`components/Forms/PayementForm`,
 * since removed), request for request: a code is looked up when the reader
 * applies it, `/student/subscribe` is posted with whatever code is in the box,
 * and a package with something left to pay goes on to the payment host while a
 * free one is simply done.
 *
 * ONE THING IT DOES DIFFERENTLY
 *
 * Editing the code drops the discount it had earned. The old form kept showing
 * it, so a code applied and then changed left a total on screen that belonged
 * to a code no longer in the box — and that total is what decides between the
 * payment host and "done". Here the receipt only ever describes the code the
 * reader can see.
 *
 * `error` is a failed subscription, and replaces the form: the two the server
 * is likeliest to send — already subscribed, and subscribed but unpaid — are
 * both answered on the subscriptions page, not by trying again.
 */
export const useCheckout = (pkg, onDone) => {
    const { t } = useTranslation();

    const packageId = packageIdOf(pkg);
    const policyId = pkg?.policyId;

    const [code, setCodeText] = useState('');
    const [promotion, setPromotion] = useState(NO_CODE);
    const [policy, setPolicy] = useState({ html: null, loading: Boolean(policyId), error: null });
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // A reply for a code the reader has since edited must not land.
    const lookupRef = useRef(0);

    // The failure is kept as its key and worded on the way out, so this effect
    // answers to the policy alone: with `t` among its dependencies, a `t` that
    // is a new function each render would fetch the policy for ever.
    useEffect(() => {
        if (!policyId) return undefined;

        let live = true;
        getPackagePolicy(policyId)
            .then((html) => { if (live) setPolicy({ html, loading: false, error: null }); })
            .catch((err) => {
                if (!live) return;
                setPolicy({ html: null, loading: false, error: err?.response ? 'ErrorBody' : 'ErrorConn' });
            });

        return () => { live = false; };
    }, [policyId]);

    const setCode = useCallback((value) => {
        lookupRef.current += 1;
        setCodeText(value);
        setPromotion(NO_CODE);
    }, []);

    const applyCode = useCallback(() => {
        const entered = code.trim();
        if (!entered) return;

        const lookup = ++lookupRef.current;
        setPromotion({ discount: 0, checking: true, note: null });

        getPromotion(entered)
            .then((found) => {
                if (lookup !== lookupRef.current) return;

                const discount = promotionDiscount(found, pkg);
                setPromotion({
                    discount,
                    checking: false,
                    note: discount > 0
                        ? { tone: 'ok', text: t('v2.checkout.codeApplied') }
                        : { tone: 'error', text: t('v2.checkout.codeNoDiscount') },
                });
            })
            .catch((err) => {
                if (lookup !== lookupRef.current) return;

                setPromotion({
                    discount: 0,
                    checking: false,
                    note: { tone: 'error', text: err?.response ? t('v2.checkout.codeInvalid') : t('ErrorConn') },
                });
            });
    }, [code, pkg, t]);

    const totals = useMemo(() => totalsOf(pkg, promotion.discount), [pkg, promotion.discount]);

    const submit = useCallback(async () => {
        setSubmitting(true);

        try {
            const { paymentId } = await subscribe({ packageId, promotionCode: code.trim() });

            if (totals.total > 0) {
                // The page is leaving for the payment host. The button stays
                // busy until it has gone, rather than coming back to life for
                // the moment in between.
                await startPaymentCheckout(paymentId);
                return;
            }

            onDone?.();
        } catch (err) {
            const reason = err?.response?.data?.code;

            if (reason === 'UserHasValidSubscriptionOnPackage') setError(t('AlreadySubscribed'));
            else if (reason === 'UserHasNotCompleteSubscriptionOnPackage') setError(t('AlreadySubscribedButNotPaid'));
            else if (err?.response) setError(t(reason || 'ErrorBody'));
            else setError(t('ErrorConn'));
        }

        setSubmitting(false);
    }, [packageId, code, totals.total, onDone, t]);

    const clearError = useCallback(() => setError(null), []);

    // The string as well as the value: the old form refused an id of `'null'`,
    // and whatever produced one then can produce one now.
    const hasPackage = packageId != null && packageId !== 'null';

    return {
        name: pkg?.packageName || pkg?.name || '',
        days: daysOf(pkg),
        totals,
        code,
        setCode,
        applyCode,
        promotion,
        needsPolicy: Boolean(policyId),
        policy: { ...policy, error: policy.error && t(policy.error) },
        agreed,
        setAgreed,
        canSubmit: hasPackage && (!policyId || agreed) && !promotion.checking && !submitting,
        submitting,
        submit,
        error,
        clearError,
    };
};
